import _ from 'underscore';
import Backbone from 'backbone';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import { focusLockInstanceDecorator } from '@bingads-webui/focus-lock-decorator';
import $ from 'jquery';
import popoverViewTemplate from './template/index.pug';

/**
 *
 * @param {number} primaryValue value that should be used when defined.
 * @param {number} backupValue value that should be used only when primaryValue is not defined.
 *
 * @return {object} { primaryValue, backupValue } for not used value, assign 'auto'
 */
export function getFormattedValue(primaryValue, backupValue) {
  let finalPrimary = primaryValue;
  let finalBackup = backupValue;

  if (_.isUndefined(finalPrimary)) {
    if (_.isUndefined(finalBackup)) {
      finalPrimary = 0;
      finalBackup = 'auto';
    } else {
      finalPrimary = 'auto';
    }
  } else {
    finalBackup = 'auto';
  }

  return {
    primaryValue: finalPrimary,
    backupValue: finalBackup,
  };
}

class OriginPopoverView extends DeterministicView {
/**
 *
 * @param {object} options the option.
 * @param {object} target the target that the popover will show for.
 * @param {Object} position the position that popover view will show besides the target.
 * @param {String} containerClassName the class name for popover view.
 *
 * @return {Void} void
 */
  initialize({
    target,
    position,
    containerClassName = '',
    isForNewUI = false,
    parent$el,
  }) {
    this.containerClassName = containerClassName;
    this.target = target;
    this.position = position;
    this.isForNewUI = isForNewUI;
    this.parent$el = parent$el;
    document.body.appendChild(this.el);

    this.setState({ content: '' });
  }

  updatePosition = (position, target) => {
    const positionVal = _.isFunction(position) ? position(this) : position;
    let {
      isAbsolute = false,
      left,
      top,
    } = positionVal;
    const { right, bottom } = positionVal;

    if (!isAbsolute) {
      left = left || 0;
      top = top || 0;
      const { left: targetLeft, top: targetTop } = $(target).offset();

      left += targetLeft;
      top += targetTop;
      isAbsolute = false;
    }

    const {
      primaryValue: finalLeft,
      backupValue: finalRight,
    } = getFormattedValue(left, right);

    const {
      primaryValue: finalTop,
      backupValue: finalBottom,
    } = getFormattedValue(top, bottom);

    this.parent$el.css({
      left: finalLeft,
      right: finalRight,
      top: finalTop,
      bottom: finalBottom,
    });
  }

  get template() {
    return ({ model }) => popoverViewTemplate({
      model,
      containerClassName: this.containerClassName,
      isForNewUI: this.isForNewUI,
    });
  }

  /**
   * Set popover's content
   * @param {Object} content set popover's content, deterministic view required
   *   for template, please use :
   *   { DeterministicViewForHtml } from '@bingads-webui/deterministic-wrapper-view'.
   *  for backbone view, please use :
   *  { DeterministicViewForBackBoneView } from '@bingads-webui/deterministic-wrapper-view'
   * @returns {PopoverView} - The view itself
   */
  setContent(content) {
    content.on('rendered', () => {
      this.updatePosition(this.position, this.target);
    });
    this.setChild('content', content);
    return this.setState({ content });
  }

  setPositionByTarget(target) {
    this.target = target;
    this.updatePosition(this.position, target);
  }
}

export class PopoverView extends Backbone.View {
  constructor(options) {
    const {
      position = 10,
    } = options;

    super(_.extend(options, {
      position,
    }));

    this.target = options.target;
    this.position = options.position;
    this.containerClassName = options.containerClassName;
    this.isForNewUI = options.isForNewUI;
    this.focusLockOptions = options.focusLockOptions;

    this.originPopoverView = new OriginPopoverView({
      target: this.target,
      position: this.position,
      containerClassName: this.containerClassName,
      isForNewUI: this.isForNewUI,
      parent$el: this.$el,
    });
    const FocusLockPopover = focusLockInstanceDecorator(this.originPopoverView, {
      onExit: () => this.remove(),
      ...this.focusLockOptions,
    }, {
      managed: true,
    });
    this.focusLockPopover = new FocusLockPopover();

    this.onClickDocument = (e) => {
      if (!this.el.contains(e.target)) {
        this.remove();
      }
    };

    document.addEventListener('click', this.onClickDocument, true);
    this.$el.css({
      position: 'absolute',
      left: 0,
      top: 0,
    });

    if (this.isForNewUI) this.$el.addClass('new-ui-popover-view');

    this.listenTo(this.originPopoverView, 'rendered', () => { this.trigger('rendered'); });
    this.on('rendered', () => { this.updatePosition(this.position, this.target); });
    this.render();
  }

  events() {
    return {
      'click .popover-view__dismiss': this.remove,
    };
  }

  render() {
    this.$el.append(this.focusLockPopover.render().el);
    document.body.appendChild(this.el);
    return this;
  }

  updatePosition(position, target) {
    this.originPopoverView.updatePosition(position, target);
  }

  remove() {
    document.removeEventListener('click', this.onClickDocument, true);
    this.trigger('remove');
    this.originPopoverView.remove();
    this.focusLockPopover.remove();
    super.remove();
  }

  setContent(content) {
    this.originPopoverView.setContent(content);
    return this;
  }

  setPositionByTarget(target) {
    this.originPopoverView.setPositionByTarget(target);
  }
}
